import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useFetch, useForm, useGrid, useModal, useResource, useStore, useToast, usePage, useAuth } from "@/comn/hooks";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, FormControl, Text } from "@/comn/components";


import { PTLI0919004S } from "@/tra/tancis/ptli/usr/frgnr/PTLI0919004S";
import { PTLI0919005S } from "@/tra/tancis/ptli/usr/frgnr/PTLI0919005S";

import {
    APIS,
    URLS,
    SF_USR_FRGNR_SRCH_FORM,
    SG_SEARCH_FRGNR_LIST,
    SG_SEARCH_FRGNR_EXCEL_LIST,
    SG_VHCL_SEARCH_FRGNR_LIST,
} from "@/tra/tancis/ptli/usr/frgnr/services/PtliUsrFrgnrService";
import dayjs from "dayjs";

/*
 * Foreign Transporter List
 * !== 외국인 운송업체 목록 ==!
 */
export const PTLI0919001S = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_cgmi0067", "auth_ptli0042", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const [load, setLoad] = useState(false);

    const location = useLocation();
    
    const [showingVhclGrid, setShowingVhclGrid] = useState(false);

    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "LCM0006" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Foreign Transporter Search Form !== 외국인 운송업체 목록 검색 Form ==!
        usrFrgnrSrchForm: useForm({
            defaultSchema: SF_USR_FRGNR_SRCH_FORM,
            defaultValues: {
                ...(pgeStore?.form || {}),
                frstRgsrDtmStr: comnUtils.getDate({ m: -1 }),
                frstRgsrDtmEnd: comnUtils.getDate(),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Foreign Transporter Search List !== 외국인 운송업체 검색 목록 ==!
        usrFrgnrSrchList: useGrid({
            defaultSchema: SG_SEARCH_FRGNR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        usrFrgnrSrchExcelList: useGrid({
            defaultSchema: SG_SEARCH_FRGNR_EXCEL_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        vhclFrgnrSrchList: useGrid({
            defaultSchema: SG_VHCL_SEARCH_FRGNR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // GET - Foreign Transporter List !== 외국 운송업자 신청 목록 조회 ==!
        getFrgnrList: useFetch({
            api: (page = grid.usrFrgnrSrchList.page) => {
                return APIS.getFrgnrList(form.usrFrgnrSrchForm.getValues(), page, grid.usrFrgnrSrchList.size);
            },
            enabled: comnUtils.isEmpty(form.usrFrgnrSrchForm.errors) && form.usrFrgnrSrchForm.isSubmitted,
            key: [grid.usrFrgnrSrchList.page, grid.usrFrgnrSrchList.size],
            onSuccess: (data) => {
                setStore(pgeUid, {
                    form: form.usrFrgnrSrchForm.getValues(),
                    page: grid.usrFrgnrSrchList.page,
                    size: grid.usrFrgnrSrchList.size,
                });
            },
            onError: (error) => {
                console.log(error);
            },
            showToast: true,
        }),
        
        // GET - Foreign Transporter List !== 외국 운송업자 신청 목록 조회 ==!
        getFrgnrExcelList: useFetch({
            api: (data: any) => (load ? APIS.getFrgnrExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.usrFrgnrSrchForm.errors),
            onSuccess: () => {
                if (load) {
                    grid.usrFrgnrSrchExcelList.exportExcel({
                        // Internal Portal Customs User Information !== 내부 포털_세관_사용자 정보 ==!
                        excelName: `${t("L_INT_PTL")}_${t("L_FRGN_TRPR")}_${t("T_USR_INFO")}_${dayjs().format(
                            "DD/MM/YYYY HHmm",
                        )}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),

        //
        // GET - Foreign Vehicle List !== 외국 운송업자 신청 목록 조회 ==!
        getFrgnrVhclList: useFetch({
            api: (page = grid.usrFrgnrSrchList.page) => {
                return APIS.getSrchFrgnrVhclList(form.usrFrgnrSrchForm.getValues(), page, grid.vhclFrgnrSrchList.size);
            },
            enabled: comnUtils.isEmpty(form.usrFrgnrSrchForm.errors) && form.usrFrgnrSrchForm.isSubmitted,
            key: [grid.vhclFrgnrSrchList.page, grid.vhclFrgnrSrchList.size],
            onSuccess: (data) => {
                setStore(pgeUid, {
                    form: form.usrFrgnrSrchForm.getValues(),
                    page: grid.vhclFrgnrSrchList.page,
                    size: grid.vhclFrgnrSrchList.size,
                });
            },
            onError: (error) => {
                console.log(error);
            },
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // GET - Foreign Transporter List !== 외국 운송업자 신청 목록 조회 ==!
        getFrgnrList: (page: number = 0) => {
            setShowingVhclGrid(false);
            form.usrFrgnrSrchForm.handleSubmit(
                (data) => {
                    form.usrFrgnrSrchForm.setValue("srchVhclYn", "N");
                    grid.usrFrgnrSrchList.setPage(page);
                    fetch.getFrgnrList.fetch(page);
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        
        // GET - Foreign Transporter List !== 외국 운송업자 신청 목록 조회 ==!
        getFrgnrVhclList: (page: number = 0) => {
            setShowingVhclGrid(true);
            form.usrFrgnrSrchForm.handleSubmit(
                (data) => {
                    grid.vhclFrgnrSrchList.setPage(page);
                    fetch.getFrgnrVhclList.fetch(page);
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
    };

    const render = {
        grid_usrFrgnrSrchList: {
            cell: {
                coNm: (props: any) => {
                    const { binding, rowValues, value } = props;
                    const coNm = `${rowValues.coNm}`;
                    //const formatTin = coTin.replace(/(\d{3})(\d{3})(\d{3})/, "$1-$2-$3");

                    return (
                        <Link to={`${URLS.ptli0919003s}/${rowValues.coTin}`} state={{ data: rowValues }}>
                            {coNm}
                        </Link>
                    );
                }
            },
        },

        grid_vhclFrgnrSrchList: {
            cell: {
                vhclRgsrNo: (props: any) => {
                    const { binding, rowValues, value } = props;

                    console.log("==============================================================")
                    console.log(fetch.getFrgnrVhclList.data);
                    console.log("----------------------------------------------------")
                    console.log(fetch.getFrgnrVhclList.data?.frgnrVhclsList);
                    console.log("==============================================================")
                    return (
                        <Text
                            underline
                            cursor="pointer"
                            onClick={() => {
                                const handleSave = (data: any) => {
                                    if (data == "update") {
                                        fetch.getFrgnrVhclList.fetch();
                                    }

                                    if (data == "delete") {
                                        fetch.getFrgnrVhclList.fetch();
                                        modal.closeModal();
                                    }
                                };
                                modal.openModal({
                                    content: (
                                        <PTLI0919005S
                                            resData={{
                                                coTin: rowValues.coTin,
                                                appNo: rowValues.appNo,
                                                amdDgr: rowValues.amdDgr,
                                                lcneYy: rowValues.lcneYy,
                                                vhclRgsrNo: rowValues.vhclRgsrNo,
                                                cardRgsrNo: rowValues.cardRgsrNo,
                                                frgnVhclRgsrBllNo: rowValues.frgnVhclRgsrBllNo,
                                                pin: rowValues.pin,
                                            }}
                                            handleSave={handleSave}
                                        />
                                    ),
                                    size: "md",
                                    draggable: true,
                                    close: false,
                                });
                            }}
                        >{`${rowValues.vhclRgsrNo}`}</Text>
                    );
                },
            },
        },
    };

    const [srchVhclYnStat, setSrchVhclYn] = useState('N');   


    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.getFrgnrList(pgeStore?.page);
    }, []);

    /**
     * Derive the active mode directly from the form field at call time.
     * This is the single source of truth — no stale useState mirror.
     */
    const [isVhclMode, setIsVhclMode] = useState(false);
    useEffect(() => {
        const srchVhclYn = form.usrFrgnrSrchForm.watch("srchVhclYn")??"N";
        setIsVhclMode(srchVhclYn == "Y");
        console.log("-------------------------------------")
        console.log(isVhclMode ? "Vehicle mode :" + srchVhclYn : "User mode :" + srchVhclYn);
    }, [isVhclMode, form.usrFrgnrSrchForm.watch("srchVhclYn")]);


    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getFrgnrExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.usrFrgnrSrchForm.schema.sbmtDt} controlSize={10}></Group.Control>
                            {/*<Group.Control {...form.usrFrgnrSrchForm.schema.coTin}></Group.Control>*/}
                        </Group.Row>

                        <Group.Row>
                            <Group.Control {...form.usrFrgnrSrchForm.schema.pin}></Group.Control>
                            <Group.Control {...form.usrFrgnrSrchForm.schema.lcneYy}></Group.Control>
                        </Group.Row>

                        <Group.Row>
                            <Group.Control {...form.usrFrgnrSrchForm.schema.coNm}></Group.Control>
                            <Group.Control {...form.usrFrgnrSrchForm.schema.lcneAplyPrcssStatCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.usrFrgnrSrchForm.schema.vhclRgsrNo} letterCase="upper"></Group.Control>
                            <Group.Control {...form.usrFrgnrSrchForm.schema.srchVhclYn} ></Group.Control>
                        </Group.Row>

                    </Group.Section>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="reset"
                                onClick={() => {
                                    form.usrFrgnrSrchForm.reset();
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    isVhclMode ? handler.getFrgnrVhclList() : handler.getFrgnrList();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    setLoad(true);
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout>
                    <Layout hidden={!showingVhclGrid}>                        
                        <Grid 
                            {...grid.vhclFrgnrSrchList.grid}
                            render={render.grid_vhclFrgnrSrchList}
                            data={fetch.getFrgnrVhclList.data?.frgnrVhclsList}
                        /> 
                    </Layout>
                    <Layout hidden={showingVhclGrid}>
                        <Grid 
                            {...grid.usrFrgnrSrchList.grid}
                            render={render.grid_usrFrgnrSrchList}
                            data={fetch.getFrgnrList.data?.frgnrList}
                        />
                    </Layout>
                </Group.Body>
            </Group>
                                
            <Layout>
                <Layout.Left>
                    <Layout.Button
                        role="new"
                        onClick={() => {
                            navigate(URLS.ptli0919002s);
                        }}
                    />
                </Layout.Left>
            </Layout>
            <Group hidden>
                <Grid {...grid.usrFrgnrSrchExcelList.grid} data={fetch.getFrgnrExcelList.data?.frgnrExcelList} />
            </Group>
        </Page>
    );
};
